import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Search, Globe, MapPin, Phone, Mail, Tag, BookOpen, Send, ExternalLink } from 'lucide-react';
import { useToast } from '../components/Toast';
import Modal from '../components/Modal';
import api from '../services/api';

export default function DirectoryPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [tenants, setTenants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [requestTarget, setRequestTarget] = useState(null);
  const [requestMsg, setRequestMsg] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      const res = await api.get('/system/directory', { params });
      setTenants(res.data.tenants);
      setCategories(res.data.categories || []);
    } catch { toast.error(t('directory.loadingError')); }
    finally { setLoading(false); }
  }, [search, category, toast]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const handleRequestPricelist = async () => {
    if (!requestTarget) return;
    setSending(true);
    try {
      const res = await api.post(`/system/directory/${requestTarget.id}/request-pricelist`, { message: requestMsg });
      toast.success(t('directory.requestSent', { name: res.data.admin_name }));
      setRequestTarget(null);
      setRequestMsg('');
    } catch (err) {
      toast.error(err.response?.data?.error || t('common.error'));
    } finally { setSending(false); }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          <Building2 size={28} className="text-indigo-600" /> {t('directory.title')}
        </h1>
        <p className="text-gray-500 mt-1">{t('directory.subtitle')}</p>
      </div>

      {/* Search & filters */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('directory.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
        </div>
        {categories.length > 0 && (
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm min-w-[180px]">
            <option value="">{t('directory.allCategories')}</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Building2 size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg">{search || category ? t('common.noResults') : t('directory.noCompanies')}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {tenants.map(tn => (
            <div key={tn.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition group">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
                      <Building2 size={24} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-800">{tn.company_full_name || tn.name}</h2>
                      {tn.company_full_name && tn.name !== tn.company_full_name && (
                        <span className="text-sm text-gray-500">{tn.name}</span>
                      )}
                    </div>
                  </div>

                  {tn.business_description && (
                    <p className="text-sm text-gray-600 mb-3 leading-relaxed">{tn.business_description}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    {tn.business_category && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full">
                        <Tag size={10} /> {tn.business_category}
                      </span>
                    )}
                    {tn.address_city && (
                      <span className="flex items-center gap-1"><MapPin size={12} /> {tn.address_city}</span>
                    )}
                    {tn.contact_email && (
                      <span className="flex items-center gap-1"><Mail size={12} /> {tn.contact_email}</span>
                    )}
                    {tn.contact_phone && (
                      <span className="flex items-center gap-1"><Phone size={12} /> {tn.contact_phone}</span>
                    )}
                    {tn.website && (
                      <a href={tn.website.startsWith('http') ? tn.website : `https://${tn.website}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline">
                        <Globe size={12} /> {tn.website}
                      </a>
                    )}
                  </div>

                  {tn.business_tags && tn.business_tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {tn.business_tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>

                <button onClick={() => { setRequestTarget(tn); setRequestMsg(''); }}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium ml-4 flex-shrink-0 opacity-80 group-hover:opacity-100 transition">
                  <BookOpen size={14} /> {t('directory.requestPricelist')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Request pricelist modal */}
      <Modal isOpen={!!requestTarget} onClose={() => setRequestTarget(null)}
        title={t('directory.requestPricelistTitle', { name: requestTarget?.company_full_name || requestTarget?.name })}>
        <div className="space-y-4">
          <p className="text-sm text-gray-500" dangerouslySetInnerHTML={{ __html: t('directory.requestMessage', { name: requestTarget?.company_full_name || requestTarget?.name }) }} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('directory.requestMessageLabel')}</label>
            <textarea value={requestMsg} onChange={e => setRequestMsg(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={5}
              placeholder={t('directory.requestMessagePlaceholder')} />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setRequestTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">{t('common.cancel')}</button>
            <button onClick={handleRequestPricelist} disabled={sending}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm">
              <Send size={14} /> {sending ? t('directory.sending') : t('directory.sendRequest')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
